import React, { ElementType } from 'react'
import { useTranslation } from 'react-i18next'
import { config } from '@config/config'
import { AnalyticsElement } from '@typesApp/Analytics'
import { ContactUsVariant } from '@components/interactionContext/types'
import { IconAnchor } from '@components/ui-css/IconAnchor'
import { IconButtonLink } from '@components/ui-css/IconButton'
import { useEnvironment } from '@hooks/useEnvironment'
import { useWebchat } from '@hooks/useWebchat'
import { openNewTab } from '@utils/openNewTab'
import { injectDataAttributes } from '@utils/dataAttributes'
import { injectAnalytics } from '@utils/analyticsAttributes'
import styles from '../styles/Error.module.scss'

type ActionConfig = {
  variant: ContactUsVariant
  Action: ElementType
}

export const ContactUsPanel: React.FC = () => {
  const configOptions = config.getFeatures()?.contactUs
  const showCallUs = !configOptions || configOptions?.includes('phone')
  const showChat = !configOptions || configOptions?.includes('chat')
  const showFaq = !configOptions || configOptions?.includes('faq')

  const actions: ActionConfig[] = []
  showCallUs && actions.push({ variant: 'phone', Action: CallUsAction })
  showChat && actions.push({ variant: 'chat', Action: ChatAction })
  showFaq && actions.push({ variant: 'faq', Action: FAQAction })

  return (
    <div className={styles.contactUs}>
      {actions.map(({ variant, Action }, index) => (
        <Action key={variant} analyticsIndex={index + 1} />
      ))}
    </div>
  )
}

type ActionProps = {
  analyticsIndex: number
}

const CallUsAction: React.FC<ActionProps> = ({ analyticsIndex }) => {
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace)

  return (
    <IconAnchor
      href={`tel:${t('ContactInfo.phone')}`}
      underline
      iconProps={{ library: 'base', name: 'phone' }}
      preLink={t('ErrorPage.footer.phone-prelink')}
      preLinkClassName={styles.preLink}
      {...injectDataAttributes('contactUs.phone')}
      {...injectAnalytics(AnalyticsElement.MORE_INFO, { index: analyticsIndex })}
    >
      <span className={styles.linkText}>{t('ErrorPage.footer.phone')}</span>
    </IconAnchor>
  )
}

const ChatAction: React.FC<ActionProps> = ({ analyticsIndex }) => {
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace)
  const { toggleWebchat } = useWebchat()

  const onClick = () => {
    toggleWebchat()
  }

  return (
    <IconButtonLink
      onClick={onClick}
      underline
      iconProps={{ library: 'base', name: 'chat' }}
      preLink={t('ErrorPage.footer.chat-prelink')}
      preLinkClassName={styles.preLink}
      {...injectDataAttributes('contactUs.chat')}
      {...injectAnalytics(AnalyticsElement.MORE_INFO, { index: analyticsIndex })}
    >
      <span className={styles.linkText}>{t('ErrorPage.footer.chat')}</span>
    </IconButtonLink>
  )
}

const FAQAction: React.FC<ActionProps> = ({ analyticsIndex }) => {
  const { faqUrl, translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace)

  const onClick = () => {
    faqUrl && openNewTab(faqUrl)
  }

  return (
    <IconButtonLink
      onClick={onClick}
      underline
      iconProps={{ library: 'base', name: 'faq' }}
      preLink={t('ErrorPage.footer.faq-prelink')}
      preLinkClassName={styles.preLink}
      {...injectDataAttributes('contactUs.faq')}
      {...injectAnalytics(AnalyticsElement.MORE_INFO, { index: analyticsIndex })}
    >
      <span className={styles.linkText}>{t('ErrorPage.footer.faq')}</span>
    </IconButtonLink>
  )
}
